import React, { useEffect, useState } from "react";
import { Wand2, Mail, Eye, MousePointerClick, Coins, Users, Contact, X, Loader2, Trophy, RefreshCw, Send } from "lucide-react";
import { AdminShell } from "./AdminShell";
import { authApi } from "../authApi";

const RANGES = [["7d", "7 jours"], ["30d", "30 jours"], ["90d", "90 jours"], ["all", "Tout"]];
const CRITERIA = [["score", "Score de performance"], ["commission", "Commissions"], ["click_rate", "Taux de clic"], ["sent", "E-mails envoyés"]];
const eur = (n) => `${(Number(n) || 0).toLocaleString("fr-FR", { maximumFractionDigits: 2 })} €`;
const dt = (s) => { try { return new Date(s).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return "—"; } };
const monthLabel = (ym) => { try { const [y, m] = ym.split("-"); return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString("fr-FR", { month: "long", year: "numeric" }); } catch { return ym; } };
const metricVal = (a, crit) => crit === "commission" ? eur(a.commission_eur) : crit === "click_rate" ? `${a.click_rate}%` : crit === "sent" ? `${a.sent} envois` : `${a.score} pts`;

function EmailModal({ id, onClose }) {
  const [e, setE] = useState(null);
  useEffect(() => { authApi.agentEmailDetail(id).then(setE).catch(() => setE({ error: true })); }, [id]);
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="admin-agent-email-modal">
      <div onClick={(ev) => ev.stopPropagation()} className="w-full max-w-xl bg-white rounded-[10px] shadow-2xl overflow-hidden flex flex-col" style={{ maxHeight: "90vh" }}>
        <div className="px-5 py-3 border-b border-zinc-100 flex items-center justify-between gap-2 shrink-0">
          <p className="text-sm font-semibold text-[#111827] flex items-center gap-2"><Mail className="w-4 h-4 text-[#C19A6B]" /> {e && e.subject ? e.subject : "E-mail"}</p>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-700"><X className="w-5 h-5" /></button>
        </div>
        {!e ? <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-[#C19A6B]" /></div> : e.error ? <div className="p-8 text-center text-sm text-zinc-500">E-mail indisponible.</div> : (
          <>
            <p className="px-5 pt-3 text-[11px] text-zinc-500 shrink-0">{dt(e.sent_at)} · Prospect {e.prospect_label || e.prospect_masked} · Source {e.source} · {(e.open_count || 0) > 0 ? "Ouvert" : "Non ouvert"} · {e.click_count || 0} clic(s) · Coût IA {eur(e.ai_cost_eur)}{e.source_url ? <> · <a href={e.source_url} target="_blank" rel="noopener noreferrer" className="text-[#C19A6B] underline">source</a></> : null}</p>
            <div className="p-4 overflow-auto flex-1"><iframe title="email" srcDoc={e.html || ""} className="w-full rounded-md border border-zinc-100" style={{ height: "62vh" }} data-testid="admin-agent-email-iframe" /></div>
          </>
        )}
      </div>
    </div>
  );
}

function Leaderboard() {
  const [month, setMonth] = useState("");
  const [criteria, setCriteria] = useState("score");
  const [data, setData] = useState(null);
  useEffect(() => { setData(null); authApi.agentLeaderboard(month, criteria).then(setData).catch(() => setData({ advisors: [], months: [] })); }, [month, criteria]);
  const adv = (data && data.advisors) || [];
  const podium = adv.slice(0, 3);
  const rest = adv.slice(3);
  const medalColor = ["text-[#F5C451]", "text-zinc-300", "text-[#CD7F32]"];
  return (
    <div className="mb-10" data-testid="admin-agent-leaderboard">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h3 className="text-sm font-semibold text-white flex items-center gap-2"><Trophy className="w-4 h-4 text-[#F5C451]" /> Podium des conseillers — {monthLabel((data && data.month) || month || new Date().toISOString().slice(0, 7))}</h3>
        <div className="flex flex-wrap gap-2">
          <select value={criteria} onChange={(e) => setCriteria(e.target.value)} className="bg-[#111114] border border-white/15 text-xs text-zinc-200 rounded-[5px] px-2 py-1.5" data-testid="admin-agent-lb-criteria">
            {CRITERIA.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          <select value={month} onChange={(e) => setMonth(e.target.value)} className="bg-[#111114] border border-white/15 text-xs text-zinc-200 rounded-[5px] px-2 py-1.5" data-testid="admin-agent-lb-month">
            <option value="">Mois en cours</option>
            {((data && data.months) || []).map((mm) => <option key={mm} value={mm}>{monthLabel(mm)}</option>)}
          </select>
        </div>
      </div>
      {!data ? <p className="text-sm text-[#C19A6B] uppercase tracking-widest animate-pulse">Chargement…</p>
        : adv.length === 0 ? <div className="bg-[#111114] border border-white/10 p-10 text-center text-sm text-zinc-400">Aucune activité Agent IA ce mois-ci.</div>
          : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3" data-testid="admin-agent-podium">
                {podium.map((a, i) => (
                  <div key={a.member_id} className={`relative border p-4 rounded-[6px] ${i === 0 ? "border-[#C19A6B]/60 bg-gradient-to-br from-[#1a1712] to-[#111114]" : "border-white/10 bg-[#111114]"}`} data-testid={`admin-agent-podium-${i + 1}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <Trophy className={`w-5 h-5 ${medalColor[i]}`} />
                      <span className="text-[11px] uppercase tracking-wider text-zinc-500">{i + 1}ᵉ place</span>
                    </div>
                    <p className="text-lg font-medium text-white truncate">{a.name}</p>
                    <p className="text-2xl font-light text-[#C19A6B] mt-1">{metricVal(a, criteria)}</p>
                    <div className="flex flex-wrap gap-x-3 gap-y-1 mt-3 text-[11px] text-zinc-400">
                      <span>{a.sent} envois</span><span>{a.open_rate}% ouv.</span><span>{a.click_rate}% clic</span><span>{eur(a.commission_eur)}</span>
                    </div>
                  </div>
                ))}
              </div>
              {rest.length > 0 && (
                <div className="bg-[#111114] border border-white/10 divide-y divide-white/5" data-testid="admin-agent-lb-rest">
                  {rest.map((a, i) => (
                    <div key={a.member_id} className="flex items-center gap-4 px-4 py-2.5">
                      <span className="text-xs text-zinc-500 w-6 shrink-0">{i + 4}</span>
                      <span className="text-sm text-white flex-1 min-w-0 truncate">{a.name}</span>
                      <span className="text-xs text-zinc-400 shrink-0">{a.sent} env · {a.open_rate}% · {a.click_rate}%</span>
                      <span className="text-sm text-[#C19A6B] shrink-0 w-24 text-right">{metricVal(a, criteria)}</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
    </div>
  );
}

export default function AdminAgent({ embedded } = {}) {
  const [range, setRange] = useState("all");
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [viewId, setViewId] = useState(null);
  const [relance, setRelance] = useState(null); // {busy} | {done, summary}
  const runRelances = async () => {
    setRelance({ busy: true });
    try { const s = await authApi.agentRunRelances(); setRelance({ done: true, summary: s }); authApi.agentOverview(range).then(setData).catch(() => {}); }
    catch (e) { setRelance({ error: (e && e.message) || "Échec" }); }
  };
  useEffect(() => { setData(null); setErr(""); authApi.agentOverview(range).then(setData).catch((e) => setErr(e.message || "Accès refusé")); }, [range]);
  const t = (data && data.totals) || {};
  const tiles = [
    { l: "Conseillers", v: t.advisors || 0, i: Users },
    { l: "Prospects", v: t.prospects || 0, i: Contact },
    { l: "E-mails envoyés", v: t.sent || 0, i: Mail },
    { l: "Taux d'ouverture", v: `${t.open_rate || 0}%`, i: Eye },
    { l: "Taux de clic", v: `${t.click_rate || 0}%`, i: MousePointerClick },
    { l: "Coût IA cumulé", v: eur(t.ai_cost_eur), i: Wand2 },
    { l: "Commissions réseau", v: eur(t.commission_eur), i: Coins },
  ];
  return (
    <AdminShell embedded={embedded} title="Agent IA — vue réseau (Super Admin)">
      <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="admin-agent-ranges">
        {RANGES.map(([k, lbl]) => (
          <button key={k} onClick={() => setRange(k)} className={`px-3 py-1.5 text-xs font-semibold rounded-full transition-colors ${range === k ? "bg-[#C19A6B] text-white" : "bg-white/5 text-zinc-400 hover:text-white"}`} data-testid={`admin-agent-range-${k}`}>{lbl}</button>
        ))}
        <div className="flex-1" />
        <button onClick={runRelances} disabled={relance && relance.busy} className="inline-flex items-center gap-1.5 bg-[#111827] hover:bg-black border border-[#C19A6B]/40 text-white text-xs font-semibold rounded-full px-3 py-1.5 transition-colors disabled:opacity-50" data-testid="admin-agent-run-relances">
          {relance && relance.busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 text-[#C19A6B]" />} Lancer les relances
        </button>
      </div>
      {relance && relance.done && (
        <div className="mb-6 text-xs text-[#C19A6B] bg-[#C19A6B]/10 border border-[#C19A6B]/30 rounded-[5px] px-3 py-2 flex items-center gap-2" data-testid="admin-agent-relance-result">
          <Send className="w-3.5 h-3.5" /> Relances : {relance.summary.created} créée(s) — {relance.summary.sent} envoyée(s), {relance.summary.queued} en file, {relance.summary.skipped} ignorée(s) sur {relance.summary.scanned} candidat(s).
        </div>
      )}
      {relance && relance.error && <div className="mb-6 text-xs text-red-400" data-testid="admin-agent-relance-error">{relance.error}</div>}
      {err ? <p className="text-red-400 text-sm">{err} — réservé au super administrateur.</p>
        : !data ? <p className="text-sm text-[#C19A6B] uppercase tracking-widest animate-pulse">Chargement…</p>
          : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 mb-8" data-testid="admin-agent-tiles">
                {tiles.map((x) => (
                  <div key={x.l} className="bg-[#111114] border border-white/10 p-4">
                    <x.i className="w-4 h-4 text-[#C19A6B] mb-2" />
                    <div className="text-2xl font-light text-white">{x.v}</div>
                    <div className="text-[11px] text-zinc-500 mt-1">{x.l}</div>
                  </div>
                ))}
              </div>

              <Leaderboard />

              <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2"><Users className="w-4 h-4 text-[#C19A6B]" /> Performance par conseiller</h3>
              {(data.advisors || []).length === 0 ? (
                <div className="bg-[#111114] border border-white/10 p-10 text-center text-sm text-zinc-400 mb-8">Aucune activité Agent IA sur cette période.</div>
              ) : (
                <div className="overflow-x-auto bg-[#111114] border border-white/10 mb-8" data-testid="admin-agent-advisors">
                  <table className="w-full text-sm">
                    <thead><tr className="text-left text-[10px] uppercase tracking-wider text-zinc-500 border-b border-white/10">
                      <th className="p-3">Conseiller</th><th className="p-3">Mode</th><th className="p-3">Prospects</th><th className="p-3">Envoyés</th><th className="p-3">En attente</th><th className="p-3">Ouv.</th><th className="p-3">Clic</th><th className="p-3">Coût IA</th><th className="p-3">Commissions</th>
                    </tr></thead>
                    <tbody>
                      {data.advisors.map((a) => (
                        <tr key={a.member_id} className="border-b border-white/5 last:border-0 hover:bg-white/[0.03]" data-testid={`admin-agent-advisor-${a.member_id}`}>
                          <td className="p-3 text-white font-medium">{a.name}</td>
                          <td className="p-3"><span className={`text-[10px] px-2 py-0.5 rounded-full ${a.agent_mode === "auto" ? "bg-[#C19A6B]/20 text-[#C19A6B]" : "bg-white/10 text-zinc-300"}`}>{a.agent_mode === "auto" ? "Automatique" : "Validation"}</span></td>
                          <td className="p-3 text-zinc-300">{a.prospects}</td>
                          <td className="p-3 text-zinc-300">{a.sent}</td>
                          <td className="p-3">{a.pending > 0 ? <span className="text-amber-400 font-semibold">{a.pending}</span> : <span className="text-zinc-600">—</span>}</td>
                          <td className="p-3 text-zinc-300">{a.open_rate}%</td>
                          <td className="p-3 text-zinc-300">{a.click_rate}%</td>
                          <td className="p-3 text-zinc-400">{eur(a.ai_cost_eur)}</td>
                          <td className="p-3 text-[#C19A6B] font-medium">{eur(a.commission_eur)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2"><Mail className="w-4 h-4 text-[#C19A6B]" /> E-mails récents (tout le réseau)</h3>
              {(data.recent || []).length === 0 ? (
                <div className="bg-[#111114] border border-white/10 p-10 text-center text-sm text-zinc-400">Aucun e-mail sur cette période.</div>
              ) : (
                <div className="bg-[#111114] border border-white/10 divide-y divide-white/5" data-testid="admin-agent-recent">
                  {data.recent.map((e) => (
                    <div key={e.id} className="flex items-center gap-4 px-4 py-3" data-testid={`admin-agent-email-${e.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-white truncate">{e.advisor_name} <span className="text-zinc-600">→</span> {e.prospect_label || e.prospect_masked || "Prospect"} <span className="text-zinc-600">·</span> <span className="text-zinc-400">{e.title || "—"}</span></div>
                        <div className="text-xs text-zinc-500 truncate">{dt(e.sent_at || e.created_at)} · {e.subject}</div>
                      </div>
                      {e.status === "sent" ? <span className="text-[10px] px-2 py-1 border border-green-500/30 text-green-400 shrink-0">Envoyé</span>
                        : e.status === "pending" ? <span className="text-[10px] px-2 py-1 border border-amber-500/30 text-amber-400 shrink-0">En attente</span>
                          : e.status === "rejected" ? <span className="text-[10px] px-2 py-1 border border-white/15 text-zinc-400 shrink-0">Rejeté</span>
                            : <span className="text-[10px] px-2 py-1 border border-red-500/30 text-red-400 shrink-0">Échec</span>}
                      <span className="text-xs text-zinc-500 shrink-0 hidden sm:inline">{(e.open_count || 0) > 0 ? "ouvert" : "—"} · {e.click_count || 0} clic</span>
                      <button onClick={() => setViewId(e.id)} className="text-[11px] font-semibold text-[#C19A6B] hover:underline shrink-0" data-testid={`admin-agent-view-${e.id}`}>Lire</button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
      {viewId && <EmailModal id={viewId} onClose={() => setViewId(null)} />}
    </AdminShell>
  );
}
